import React, { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";

// Custom sanitize schema — start from the default, restrict img schemes,
// and allow target/rel on anchors. Block iframes, scripts, on-handlers fully.
const SANITIZE_SCHEMA = {
  ...defaultSchema,
  tagNames: (defaultSchema.tagNames || []).filter(t => !["iframe", "script", "object", "embed"].includes(t)),
  attributes: {
    ...defaultSchema.attributes,
    a: [...(defaultSchema.attributes?.a || []), ["target"], ["rel"]],
    img: [...(defaultSchema.attributes?.img || []), ["alt"], ["title"]],
  },
  protocols: {
    ...(defaultSchema.protocols || {}),
    href: ["http", "https", "mailto"],
    src: ["http", "https", "data"],
  },
};

/**
 * Safe markdown renderer for ALL user-visible rich text:
 *   - AI chat replies (Claude responses)
 *   - Lesson AI summaries / key takeaways
 *   - Community + Internal post bodies + comments
 *   - Course descriptions on sales pages
 *   - Support ticket messages
 *
 * Sanitizes everything via rehype-sanitize so it's safe for user-supplied content.
 *
 * Props:
 *   - children: markdown string
 *   - variant: "light" | "dark"  — switches color tokens to match container
 *   - className: extra wrapper classes
 */
export default function MarkdownText({ children, variant = "dark", className = "" }) {
  const md = typeof children === "string" ? children : (children ?? "");
  const isLight = variant === "light";
  const components = useMemo(() => ({
    a: ({ node, ...props }) => (
      <a {...props} target="_blank" rel="noopener noreferrer" className={isLight ? "text-fc-red underline hover:opacity-80" : "text-fc-red underline hover:opacity-80"} />
    ),
    code: ({ inline, className: cn, children: c, ...props }) => {
      if (inline) {
        return (
          <code
            className={`px-1.5 py-0.5 rounded text-[0.9em] font-mono ${isLight ? "bg-gray-100 text-gray-800" : "bg-white/10 text-white"}`}
            {...props}
          >
            {c}
          </code>
        );
      }
      return (
        <code className="font-mono text-[0.9em]" {...props}>{c}</code>
      );
    },
    pre: ({ children: c, ...props }) => (
      <pre className={`p-3 my-3 rounded overflow-x-auto text-[0.85em] leading-relaxed ${isLight ? "bg-[#0c1130] text-[#E5E7EB]" : "bg-black/40 text-white/90"}`} {...props}>
        {c}
      </pre>
    ),
    ul: ({ children: c }) => <ul className="list-disc pl-5 my-2 space-y-1">{c}</ul>,
    ol: ({ children: c }) => <ol className="list-decimal pl-5 my-2 space-y-1">{c}</ol>,
    li: ({ children: c }) => <li className="leading-relaxed">{c}</li>,
    h1: ({ children: c }) => <h1 className={`text-2xl font-black tracking-tight my-3 ${isLight ? "text-fc-navy" : "text-white"}`}>{c}</h1>,
    h2: ({ children: c }) => <h2 className={`text-xl font-black tracking-tight my-3 ${isLight ? "text-fc-navy" : "text-white"}`}>{c}</h2>,
    h3: ({ children: c }) => <h3 className={`text-lg font-bold tracking-tight my-2 ${isLight ? "text-fc-navy" : "text-white"}`}>{c}</h3>,
    table: ({ children: c }) => (
      <div className="overflow-x-auto my-3">
        <table className={`w-full text-sm border-collapse ${isLight ? "" : "text-white/85"}`}>{c}</table>
      </div>
    ),
    thead: ({ children: c }) => <thead className={isLight ? "bg-gray-50" : "bg-white/5"}>{c}</thead>,
    th: ({ children: c }) => <th className={`text-left px-3 py-2 font-bold border ${isLight ? "border-gray-200" : "border-white/10"}`}>{c}</th>,
    td: ({ children: c }) => <td className={`px-3 py-2 border ${isLight ? "border-gray-200" : "border-white/10"}`}>{c}</td>,
    img: ({ src, alt, ...props }) => {
      // Block non-safe protocols inline (rehype-sanitize handles too)
      if (typeof src === "string" && !/^(https?:|data:)/i.test(src)) return null;
      return <img src={src} alt={alt || ""} className="max-w-full rounded-lg my-2" loading="lazy" {...props} />;
    },
    p: ({ children: c }) => <p className="my-2 leading-relaxed">{c}</p>,
    strong: ({ children: c }) => <strong className={isLight ? "font-bold text-fc-navy" : "font-bold text-white"}>{c}</strong>,
    em: ({ children: c }) => <em className="italic">{c}</em>,
    blockquote: ({ children: c }) => (
      <blockquote className={`border-l-4 pl-3 my-3 italic ${isLight ? "border-fc-red text-gray-600" : "border-fc-red text-white/70"}`}>
        {c}
      </blockquote>
    ),
    hr: () => <hr className={`my-4 ${isLight ? "border-gray-200" : "border-white/10"}`} />,
  }), [isLight]);

  return (
    <div className={`fc-markdown ${isLight ? "text-fc-navy" : "text-white/85"} ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[[rehypeSanitize, SANITIZE_SCHEMA]]}
        components={components}
      >
        {md}
      </ReactMarkdown>
    </div>
  );
}
